import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '@/utils/store';
import Modal from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import MemberMultiSelect from '@/components/ui/MemberMultiSelect';

const BANK_COLORS = ['#004481', '#ec0000', '#007e3a', '#ff6600', '#1a1a2e', '#6366f1', '#eab308', '#0ea5e9', '#e11d48'];

const PRESET_BANKS = [
  { name: 'Banco de Chile', color: '#004481' },
  { name: 'BancoEstado', color: '#007e3a' },
  { name: 'Santander', color: '#ec0000' },
  { name: 'BCI', color: '#ff6600' },
  { name: 'Scotiabank', color: '#ec0000' },
  { name: 'Itaú', color: '#ff6600' },
  { name: 'BBVA', color: '#004481' },
  { name: 'Falabella', color: '#007e3a' },
  { name: 'Ripley', color: '#6366f1' },
  { name: 'BICE', color: '#1a1a2e' },
  { name: 'Security', color: '#0ea5e9' },
  { name: 'Consorcio', color: '#eab308' },
];

export default function AddBankModal({ open, onClose, bank }) {
  const { t } = useTranslation();
  const addBank = useStore((s) => s.addBank);
  const updateBank = useStore((s) => s.updateBank);
  const isEdit = !!bank;

  const [name, setName] = useState('');
  const [color, setColor] = useState(BANK_COLORS[0]);
  const [memberIds, setMemberIds] = useState([]);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const errs = {};
    if (!name.trim()) errs.name = t('family.form.nameRequired');
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;
    const data = { name: name.trim(), color, memberIds };
    if (isEdit) {
      updateBank(bank.id, data);
    } else {
      addBank(data);
    }
    handleClose();
  };

  const handleClose = () => {
    setName('');
    setColor(BANK_COLORS[0]);
    setMemberIds([]);
    setErrors({});
    onClose();
  };

  const selectPreset = (preset) => {
    setName(preset.name);
    setColor(preset.color);
    setErrors({});
  };

  useEffect(() => {
    if (bank) {
      setName(bank.name);
      setColor(bank.color);
      setMemberIds(bank.memberIds || []);
    }
  }, [bank]);

  return (
    <Modal open={open} onClose={handleClose} title={isEdit ? t('common.edit') : t('credits.addBank')} footer={
      <Button className="w-full" onClick={handleSave}>
        {t('common.save')}
      </Button>
    }>
      <div className="space-y-4">
        {/* Quick-select preset banks */}
        {!isEdit && (
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
              {t('credits.form.quickSelect')}
            </label>
            <div className="flex flex-wrap gap-1.5">
              {PRESET_BANKS.map((preset) => (
                <button
                  key={preset.name}
                  onClick={() => selectPreset(preset)}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                    name === preset.name
                      ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10 text-brand-700 dark:text-brand-400'
                      : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-brand-400'
                  }`}
                >
                  <span
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: preset.color }}
                  />
                  {preset.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Bank name input */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('credits.form.bankName')}
          </label>
          <input
            type="text"
            className={`input ${errors.name ? 'border-danger-500' : ''}`}
            placeholder={t('credits.form.otherBank')}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          {errors.name && <p className="text-xs text-danger-500 mt-1">{errors.name}</p>}
        </div>

        {/* Color picker */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('credits.form.bankColor')}
          </label>
          <div className="flex flex-wrap gap-3">
            {BANK_COLORS.map((c) => (
              <button
                key={c}
                onClick={() => setColor(c)}
                className={`w-10 h-10 rounded-xl transition-all ${
                  color === c ? 'ring-2 ring-offset-2 ring-brand-500 dark:ring-offset-gray-900' : ''
                }`}
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
        </div>

        {/* Member association */}
        <MemberMultiSelect
          value={memberIds}
          onChange={setMemberIds}
          label={t('credits.form.member')}
        />

      </div>
    </Modal>
  );
}
